/**
 * Routine Finder — Master Product Registry
 * ══════════════════════════════════════════
 * Single source of truth for ALL product data.
 * Every other RF file reads from window.rfProducts instead of hardcoding product info.
 *
 * To add a new product:
 *   1. Add an entry under CORE_PRODUCTS (hero-eligible) or EXTRA_PRODUCTS (Shopify-only).
 *   2. If it's hero-eligible, add its scoring rows in resultsLogic SCORES.
 *   3. If it needs personalised explanations, add entries in explanations PRODUCT_EXPLANATIONS.
 *   That's it — all other files derive their data from this registry.
 *
 * Exposes: window.rfProducts  (keyed by product ID)
 */
(function () {
  'use strict';

  // ─── Core hero-eligible products (appear in quiz results) ─────────────────
  const CORE_PRODUCTS = {
    saffron_cleanser: {
      name:       'Saffron Luminous Cleanser',
      handle:     'saffron-luminous-cleanser',
      variantId:  '44004544184536',
      price:      34,
      step:       1,
      stepName:   'Cleanse',
      tagline:    'Brightening daily cleanser',
      keyBenefit: 'Brightening & oil control',
      heroEligible: true,
      whenToUse:  { am: true, pm: true, text: 'Use up to twice daily in the AM and PM.' },
      flags:      { sensitivityTip: false, clinicallyTested: true },
      intelligence: {
        primaryStrength: 'brightening and oil control',
        keyAction:       'brightens and purifies',
        clinicalProof:   'After 28 days: 90% observed more unified skin tone, 85% felt it effectively targeted skin congestion, and 85% reported smoother, supple skin.',
        idealScenario:   'dull, oily, or congested skin needing radiance',
      },
    },

    saponins_cleanser: {
      name:       'Saponins Cream Cleanser',
      handle:     'saponins-cream-cleanser',
      variantId:  '44004527636696',
      price:      44,
      step:       1,
      stepName:   'Cleanse',
      tagline:    'Gentle barrier-support cleanser',
      keyBenefit: 'Gentle & barrier-supportive',
      heroEligible: true,
      whenToUse:  { am: true, pm: true, text: 'Use up to twice daily in the AM and PM.' },
      flags:      { sensitivityTip: false, clinicallyTested: true },
      intelligence: {
        primaryStrength: 'barrier support and gentle hydration',
        keyAction:       'soothes and hydrates',
        clinicalProof:   'After 28 days: 95% felt cleansed without dryness, 90% observed skin felt less reactive, and 85% felt it calms and comforts sensitive skin.',
        idealScenario:   'sensitive, reactive, or dehydrated skin',
      },
    },

    triphala_corrector: {
      name:       'Triphala Pigmentation Corrector',
      handle:     'triphala-pigmentation-corrector',
      variantId:  '51643834007768',
      price:      74,
      step:       2,
      stepName:   'Restore',
      tagline:    '30.5% clinical brightening complex',
      keyBenefit: '30.5% brightening complex',
      heroEligible: true,
      whenToUse:  { am: true, pm: true, text: 'Use up to twice daily in the AM and PM.' },
      flags:      { sensitivityTip: true, clinicallyTested: true },
      intelligence: {
        primaryStrength: 'hyperpigmentation correction',
        keyAction:       'clinically brightens skin by 30.5%',
        clinicalProof:   'Clinically proven 30.5% skin brightening in 28 days. 92% reported more even skin, 82% saw improved discolorations, and 91% would recommend the product.',
        idealScenario:   'dark spots, sun damage, post-acne marks',
      },
    },

    'peptide_pro-resilience_serum_concentrate_30ml': {
      name:       'Peptide Pro Resilience Serum Concentrate',
      handle:     'peptide-pro-resilience-serum-concentrate-30ml',
      variantId:  '50816178192600',
      price:      65,
      step:       2,
      stepName:   'Restore',
      tagline:    'Barrier-strengthening peptide serum',
      keyBenefit: 'Peptide barrier repair',
      heroEligible: true,
      whenToUse:  { am: true, pm: true, text: 'Use up to twice daily in the AM and PM.' },
      flags:      { sensitivityTip: false, clinicallyTested: true },
      intelligence: {
        primaryStrength: 'barrier support and skin renewal',
        keyAction:       'strengthens barrier and smooths expression lines',
        clinicalProof:   '10.5% Ayurvedic Bioflavonoid Complex with encapsulated peptides',
        idealScenario:   'weakened barrier, redness, fine lines, sensitivity',
      },
    },

    complexion_accelerator: {
      name:       'Complexion Clarifying Accelerator',
      handle:     'complexion-clarifying-accelerator',
      variantId:  '39274678386863',
      price:      63,
      step:       4,
      stepName:   'Reform',
      tagline:    '5-in-1 enzyme resurfacing',
      keyBenefit: '5-in-1 enzyme complex',
      heroEligible: true,
      whenToUse:  { am: false, pm: true, text: 'Use up to once daily in the PM. Introduce slowly, gradually building up to daily use if well tolerated.' },
      flags:      { sensitivityTip: false, clinicallyTested: false },
      intelligence: {
        primaryStrength: 'texture refinement and gentle resurfacing',
        keyAction:       'resurfaces texture and brightens tone',
        clinicalProof:   '5-in-1 exfoliating complex with enzymes',
        idealScenario:   'rough texture, enlarged pores, congestion',
      },
    },

    retinal_overnight_reform: {
      name:       'Ursolic Acid & Retinal Overnight Reform',
      handle:     'ursolic-acid-retinal-overnight-reform',
      variantId:  '39274818371759',
      price:      76,
      step:       4,
      stepName:   'Reform',
      tagline:    'Overnight renewal treatment',
      keyBenefit: '-25% wrinkle depth',
      heroEligible: true,
      whenToUse:  { am: false, pm: true, text: 'Use up to once daily in the PM. Introduce slowly, gradually building up to daily use if well tolerated.' },
      flags:      { sensitivityTip: false, clinicallyTested: true },
      intelligence: {
        primaryStrength: 'overnight renewal and skin transformation',
        keyAction:       'reduces the appearance of wrinkles by 25% in depth',
        clinicalProof:   'Clinically proven: 25% reduction in the appearance of wrinkle depth, 13.6% decrease in the appearance of crow\'s feet, 18.9% smoother skin appearance, and 8.5% reduction in the appearance of brown spots. 90% saw brighter, more radiant skin.',
        idealScenario:   'fine lines, texture issues, sun damage',
      },
    },

    future_veil_peptide_cream: {
      name:       'Future Veil\u2122 Firm & Repair Peptide Cream',
      handle:     'future-veil-firm-repair-peptide-cream-50ml',
      variantId:  '51381498314968',
      price:      89,
      step:       5,
      stepName:   'Moisturise',
      tagline:    'Collagen-boosting moisturiser',
      keyBenefit: '+311% collagen (in vitro)',
      heroEligible: true,
      whenToUse:  { am: true, pm: true, text: 'Use up to twice daily in the AM and PM.' },
      flags:      { sensitivityTip: false, clinicallyTested: true },
      intelligence: {
        primaryStrength: 'well-ageing peptide therapy and barrier repair',
        keyAction:       'clinically targets 5 markers of skin aging and fatigue',
        clinicalProof:   'Clinically proven to stimulate collagen production by up to 311% in 24 hours, reduce crow\'s feet appearance by 20.5%, improve skin firmness by 18%, boost skin elasticity by 14.5%, and reduce visible signs of skin fatigue by 18.8%.',
        idealScenario:   'fine lines, loss of firmness, dullness, skin fatigue, barrier stress',
      },
    },
  };

  // ─── Extra Shopify-only products (bundles, duos, size variants) ───────────
  // These are NOT hero-eligible — they're used by shopifyHelpers for cart/enrichment only.
  const EXTRA_PRODUCTS = {
    you_set_the_tone_duo:                           { handle: 'you-set-the-tone-duo-limited-edition',            variantId: '39274664886447' },
    you_set_the_tone_duo_ii:                        { handle: 'you-set-the-tone-duo-ii',                        variantId: '39274820599983' },
    'peptide_pro-resilience_serum_concentrate_60ml': { handle: 'peptide-pro-resilience-serum-concentrate-60ml', variantId: '50836820852952' },
    triphala_corrector_60ml:                        { handle: 'triphala-pigmentation-corrector-60ml',           variantId: '39273298067631' },
    states_of_change_journal:                       { handle: 'states-of-change-am-pm-daily-journal',           variantId: '40864776421551' },
    soothe_glow_set:                                { handle: 'soothing-glow-set',                              variantId: '43552054509784' },
    cleansing_duo:                                  { handle: 'cleansing-duo',                                  variantId: '44004548116696' },
  };

  // ─── Merge into a single registry ─────────────────────────────────────────
  const ALL = {};
  for (const [id, data] of Object.entries(CORE_PRODUCTS)) {
    ALL[id] = { ...data, id, heroEligible: true };
  }
  for (const [id, data] of Object.entries(EXTRA_PRODUCTS)) {
    ALL[id] = { ...data, id, heroEligible: false };
  }

  // ─── Expose globally ─────────────────────────────────────────────────────
  window.rfProducts = ALL;
})();
